
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TimelineItem from './TimelineItem';
import TimelineFilter from './TimelineFilter';
import { Activity, History, Clock, List } from 'lucide-react';
import { 
  type SparcInteraction,
  getUserInteractionHistory 
} from '@/services/agentic/sparcSessionService';
import { useAuth } from '@/contexts/auth/AuthContext';
import { AgentRole } from '../types';

interface AgentTimelineVisualizerProps {
  sessionId?: string;
  agentType?: AgentRole;
  limit?: number;
}

const AgentTimelineVisualizer: React.FC<AgentTimelineVisualizerProps> = ({
  sessionId,
  agentType,
  limit = 20
}) => {
  const { user } = useAuth();
  const [interactions, setInteractions] = useState<SparcInteraction[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'compact' | 'detailed'>('compact');
  const [filter, setFilter] = useState<string | null>(null);

  useEffect(() => {
    const fetchInteractions = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const fetchedInteractions = await getUserInteractionHistory(user.id);
        
        // Apply limit if needed
        const limitedInteractions = limit > 0 
          ? fetchedInteractions.slice(0, limit)
          : fetchedInteractions;
          
        // Filter by agent type if specified
        const filteredInteractions = agentType 
          ? limitedInteractions.filter(interaction => {
              // This assumes the task_name or metadata includes agent type information
              return interaction.task_name?.toLowerCase().includes(agentType.toLowerCase()) ||
                    interaction.metadata?.agentType === agentType;
            })
          : limitedInteractions;
        
        setInteractions(filteredInteractions);
      } catch (error) {
        console.error('Error fetching interaction history:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInteractions();
  }, [user, sessionId, limit, agentType]);

  // Filter interactions based on user selection
  const filteredInteractions = filter
    ? interactions.filter(interaction => 
        interaction.task_name?.toLowerCase().includes(filter.toLowerCase()) || 
        interaction.task_id === filter)
    : interactions;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <CardTitle>Agent Interaction Timeline</CardTitle>
          </div>
          <Tabs value={view} onValueChange={(v) => setView(v as 'compact' | 'detailed')} className="w-[200px]">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="compact">Compact</TabsTrigger>
              <TabsTrigger value="detailed">Detailed</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <CardDescription>
          Visualize the chronological history of agent interactions and their outcomes
        </CardDescription>
      </CardHeader>
      <CardContent>
        <TimelineFilter 
          interactions={interactions}
          onFilterChange={setFilter} 
          currentFilter={filter}
        />
        
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredInteractions.length === 0 ? (
          <div className="text-center py-10">
            <History className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-muted-foreground">No interaction history available</p>
          </div>
        ) : (
          <Tabs value={view} className="mt-4">
            <TabsContent value="compact" className="m-0">
              <div className="space-y-1">
                {filteredInteractions.map((interaction, index) => (
                  <TimelineItem 
                    key={interaction.id || index}
                    interaction={interaction}
                    isFirst={index === 0}
                    isLast={index === filteredInteractions.length - 1}
                    view="compact"
                  />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="detailed" className="m-0">
              <div className="space-y-6">
                {filteredInteractions.map((interaction, index) => (
                  <TimelineItem 
                    key={interaction.id || index}
                    interaction={interaction}
                    isFirst={index === 0}
                    isLast={index === filteredInteractions.length - 1}
                    view="detailed"
                  />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentTimelineVisualizer;
